/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  useEffect, useState, useRef, useCallback,
} from "react";
import Modal from "components/Modal";
import genericModalStyles from "helpers/genericModalStyles";
import LoginForm from "components/Forms/LoginForm";
import PropTypes from "prop-types";
import useAuth from "widgets/Onboarding/hooks/useAuth";
import "./_style.scss";
import TurnstileWrapper from "components/TurnstileWrapper";
import { TURNSTILE_KEY } from "helpers/constants";
import useLanguageProvider from "components/LanguageProvider/hooks/useLanguageProvider";

const ModalLogin = ({
  title,
  subheading,
  classes,
  onModalClose,
  openModal,
  captchaToken,
  setCaptchaToken,
}) => {
  const [isModalOpen, updateModalOpen] = useState(openModal);
  const { translate: t } = useLanguageProvider();
  const [isTurnstileInteractive, setIsTurnstileInteractive] = useState();
  const [turnstileError, setTurnstileError] = useState();
  const turnstileRef = useRef(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const refreshTurnstile = useCallback(() => {
    turnstileRef.current?.reset?.();
  }, []);

  const {
    loading: mutationLoading, error: mutationError, login, user, requiresMfa,
  } = useAuth();

  const handleSubmit = (data) => {
    if (data) {
      const mfa = data.mfaCode ? { code: data.mfaCode, type: "Authenticator" } : null;
      login({ email: data.email, password: data.password, mfa }, captchaToken);
    }
  };

  const initialCaptureToken = useRef(null);

  // set initial token or check if captcha token has refreshed
  useEffect(() => {
    if (
      captchaToken
      && (initialCaptureToken.current === null
        || initialCaptureToken.current !== captchaToken
        || (captchaToken === "XXXX.DUMMY.TOKEN.XXXX" && process.env.NODE_ENV !== "production"))
    ) {
      setIsRefreshing(false);
      initialCaptureToken.current = captchaToken;
    }
  }, [captchaToken]);

  useEffect(() => {
    if (!mutationLoading && user) {
      if (user !== null) {
        onModalClose(user);
        updateModalOpen(false);
      }
    }
  }, [user, mutationLoading, isModalOpen]);

  useEffect(() => {
    if (mutationError) {
      setIsRefreshing(true);
      refreshTurnstile();
    }
  }, [mutationError]);

  const onCloseModal = () => {
    onModalClose();
    updateModalOpen(false);
  };

  return (
    <Modal
      contentLabel="Signup/Login to continue"
      customStyle={genericModalStyles}
      isOpen={isModalOpen}
      ariaHideApp={false}
      shouldCloseOnOverlayClick
      onRequestClose={onCloseModal}
    >
      <div className="login-modal">
        <div className={`login-modal__content ${classes}`}>
          <div className="login-modal__header">
            <div className="login-modal__heading">{title}</div>
          </div>
          <div className="login-modal__body">
            {mutationError && <div className="login-modal__error-message">{mutationError}</div>}
            {subheading && <div className="login-modal__action-text">{subheading}</div>}
            {captchaToken ? (
              <LoginForm
                onSubmit={handleSubmit}
                requiresMfa={requiresMfa}
                isRefreshing={isRefreshing}
              />
            ) : (
              <div className="form signup">
                {!isTurnstileInteractive && (
                  <>
                    {["Email", "Password"].map((input) => (
                      <div className="form-row" key={input}>
                        <div className="form-group">
                          <div className="form-col">
                            <label htmlFor={`login_${input.toLowerCase()}`}>{t(input)}</label>
                            <input
                              className="input-text turnstile-skeleton"
                              disabled
                              id={`login_${input.toLowerCase()}`}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                    <div className="form-row">
                      <div className="form-group">
                        <div className="form-col">
                          {turnstileError ? (
                            <div>
                              {t(
                                "We are unable to verify you at this stage. Please try again later.",
                              )}
                            </div>
                          ) : (
                            <button
                              className="link-button --bold --primary turnstile-skeleton"
                              disabled
                            >
                              {t("Verifying...")}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
          <div className={`turnstile__wrapper ${captchaToken ? "hidden" : ""}`}>
            <TurnstileWrapper
              ref={turnstileRef}
              emitNullOnReset={false}
              onChange={(token) => setCaptchaToken({ token })}
              captchaKey={TURNSTILE_KEY}
              setTurnstileInteractive={setIsTurnstileInteractive}
              onError={setTurnstileError}
              action="modalLogin"
            />
          </div>
          <div className="login-modal__footer" />
        </div>
      </div>
    </Modal>
  );
};

ModalLogin.propTypes = {
  title: PropTypes.string.isRequired,
  classes: PropTypes.string,
  subheading: PropTypes.string,
};

export default ModalLogin;
